import { DOCUMENT } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Observable, of, EMPTY, Subscription, delay, filter, map, pluck } from 'rxjs';

import { MonitoringLogger } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';
import {
  CmsContentType,
  CmsFormType,
  CmsTemplate,
  CmsViewType,
  CmsContent,
  CmsCollection,
  CmsTaxonomy,
  CmsTaxonomyGeneral,
} from '@fcom/core-api';
import { ScrollService } from '@fcom/common';

import { findByType } from '../../../utils';

@Component({
  selector: 'fin-cms-json-content',
  templateUrl: './cms-json-content.component.html',
  styleUrls: ['./cms-json-content.component.scss', './premium-economy-brand-overrides.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CmsJsonContentComponent implements OnDestroy, OnInit {
  private route = inject(ActivatedRoute);
  private scrollService = inject(ScrollService);
  private document = inject<Document>(DOCUMENT);
  private monitoringLogger = inject(MonitoringLogger);

  @Input()
  template$: Observable<CmsTemplate> = EMPTY;
  @Input()
  pageViewType$: Observable<string>;
  @Input()
  showSideTitle$: Observable<boolean> = of(true);
  @Input()
  alternateSideImages = false;

  disruptions$: Observable<CmsCollection>;
  private imageLeftContentIds = new Set<number>();
  title$: Observable<string>;
  templateTaxonomyTags$: Observable<CmsTaxonomy[]>;

  private subscription: Subscription = new Subscription();

  public readonly DOCTYPE: typeof CmsContentType = CmsContentType;
  public readonly VIEWTYPE: typeof CmsViewType = CmsViewType;
  public readonly FORMTYPE: typeof CmsFormType = CmsFormType;
  public readonly TAXONOMY: typeof CmsTaxonomyGeneral = CmsTaxonomyGeneral;

  ngOnInit(): void {
    this.disruptions$ = this.template$.pipe(
      map((template) =>
        template?.main?.reduce((acc, curr) => {
          if (curr.contentType === CmsContentType.CMPersonalized) {
            return [...acc, ...curr.items];
          }
          return [...acc, curr];
        }, [])
      ),
      findByType(CmsViewType.DISRUPTIONS)
    );
    this.title$ = this.template$.pipe(
      pluck('header', 0),
      map((item) => (item as CmsContent)?.teaserTitle || item?.title)
    );
    this.subscription.add(
      this.route.fragment.pipe(filter(Boolean), delay(0)).subscribe((fragment): void => {
        const offset: number = this.document.querySelector('header.sticky-nav')?.clientHeight || 0;
        const element: HTMLElement = this.document.getElementById(fragment);
        this.checkSegmentValidity(fragment);

        if (element) {
          this.scrollService.smoothScroll(element, { offsetY: offset });
        }
      })
    );

    this.templateTaxonomyTags$ = this.template$.pipe(
      map((a): CmsTaxonomy[] => a?.header?.[0]?.subjectTaxonomyTags ?? [])
    );

    if (this.alternateSideImages) {
      this.subscription.add(
        this.template$.subscribe((template) => {
          this.imageLeftContentIds = this.resolveImageLeftContents(template);
        })
      );
    }
  }

  isImageLeft(content: CmsContent): boolean {
    return this.imageLeftContentIds.has(content.contentId);
  }

  private resolveImageLeftContents(template: CmsTemplate): Set<number> {
    const cmPersonalizedContents: CmsContent[] = [];
    for (const content of template?.main ?? []) {
      if (content.contentType === CmsContentType.CMPersonalized) {
        cmPersonalizedContents.push(...(content.items ?? []));
      } else {
        cmPersonalizedContents.push(content);
      }
    }

    const sideImages: CmsContent[] = cmPersonalizedContents.filter(
      (content) => content.viewType === CmsViewType.TEXT_WITH_SIDE_IMAGE
    );

    return new Set(sideImages.filter((_, index) => index % 2 === 1).map((content) => content.contentId));
  }

  // Default segment when metadata URL segment not defined is contentId #
  // Check the fragment and log instances where fragment is only a string of #'s
  private checkSegmentValidity(segment: string): void {
    const isStringOfNumbers: boolean = Number.isInteger(Number(segment));

    if (isStringOfNumbers) {
      this.monitoringLogger.warn('Page is deeplinking to content with numeric segment', {
        segment: segment,
      });
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }
}
